import { ApiClient } from '@/lib/axios/client';
import type {
  ChannelResponse,
  WebTask,
  WebTaskListResponse,
  WorkflowInstance,
  ProcessCommandRequest,
  MediaPayload,
  LocationPayload,
  TaskContext,
} from './types';
import { ActionCommands } from './types';

const BASE_PATH = '/api/v1/workflows';

export class WorkflowApi {
  /**
   * Process a web command
   */
  static async processCommand(request: ProcessCommandRequest): Promise<ChannelResponse> {
    return ApiClient.post<ChannelResponse>(`${BASE_PATH}/command`, request);
  }

  /**
   * Get current task for a workflow
   */
  static async getCurrentTask(botCode: string, sourceKey: string): Promise<WebTask | null> {
    return ApiClient.get<WebTask | null>(`${BASE_PATH}/current_task`, {
      params: { bot_code: botCode, source_key: sourceKey },
    });
  }

  /**
   * Get all tasks for a workflow
   */
  static async getWorkflowTasks(botCode: string, sourceKey: string): Promise<WebTaskListResponse> {
    return ApiClient.get<WebTaskListResponse>(`${BASE_PATH}/tasks`, {
      params: { bot_code: botCode, source_key: sourceKey },
    });
  }

  /**
   * Get active workflow instances (running workflows with pending tasks)
   * Returns the most recent instance per workflow template
   */
  static async getWorkflowInstances(): Promise<WorkflowInstance[]> {
    const response = await ApiClient.get<{ items: WorkflowInstance[] }>(`${BASE_PATH}/instances`);
    return response.items || [];
  }

  /**
   * Start a workflow
   * Note: Slash commands use the 'text' field, not 'command'
   */
  static async startWorkflow(
    userId: string,
    orgId: string,
    botCode: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: '',
      text: `/start ${botCode}`,
    });
  }

  /**
   * Submit text response (text, number, date, address task types)
   * Uses action pattern: input_{botCode}_taskId_{taskId}_repId_{repId}
   */
  static async submitText(
    userId: string,
    orgId: string,
    text: string,
    taskContext: TaskContext,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: ActionCommands.text(taskContext),
      text,
      message_type: 'text',
      session_id: sessionId,
    });
  }

  /**
   * Submit media response (photo, video, document, media task types)
   * Uses action pattern: media_{botCode}_taskId_{taskId}_repId_{repId}
   */
  static async submitMedia(
    userId: string,
    orgId: string,
    media: MediaPayload,
    taskContext: TaskContext,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: ActionCommands.media(taskContext),
      message_type: media.type,
      media,
      session_id: sessionId,
      text: media?.file_name || '', // Empty text field for media submissions
    });
  }

  /**
   * Submit location response (location, location_check task types)
   * Uses action pattern: location_{botCode}_taskId_{taskId}_repId_{repId}
   */
  static async submitLocation(
    userId: string,
    orgId: string,
    location: LocationPayload,
    taskContext: TaskContext,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: ActionCommands.location(taskContext),
      message_type: 'location',
      location,
      session_id: sessionId,
    });
  }

  /**
   * Submit choice selection
   * Uses action pattern: chatbot_{botCode}_taskId_{taskId}_repId_{repId}_choice_{choiceId}
   * @param choiceId - The note's xid (alphanumeric ID from task notes where is_custom_data is true)
   * @param choiceText - The display text of the choice (for text field)
   */
  static async submitChoice(
    userId: string,
    orgId: string,
    choiceId: string,
    choiceText: string,
    taskContext: TaskContext,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: ActionCommands.choice(taskContext, choiceId),
      text: choiceText,
      session_id: sessionId,
    });
  }

  /**
   * Skip current task
   * Uses action pattern: chatbot_{botCode}_taskId_{taskId}_skip_{repId}
   */
  static async skipTask(
    userId: string,
    orgId: string,
    taskContext: TaskContext,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: ActionCommands.skip(taskContext),
      session_id: sessionId,
    });
  }

  /**
   * Undo last action
   * Note: Slash commands use the 'text' field, not 'command'
   */
  static async undo(
    userId: string,
    orgId: string,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: '',
      text: '/undo',
      session_id: sessionId,
    });
  }

  /**
   * Exit workflow
   * Note: Slash commands use the 'text' field, not 'command'
   */
  static async exitWorkflow(
    userId: string,
    orgId: string,
    botCode: string,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: '',
      text: `/exit ${botCode}`,
      session_id: sessionId,
    });
  }

  /**
   * Get workflow summary
   * Note: Slash commands use the 'text' field, not 'command'
   */
  static async getSummary(
    userId: string,
    orgId: string,
    botCode?: string,
    sessionId?: string
  ): Promise<ChannelResponse> {
    const text = botCode ? `/summary ${botCode}` : '/summary';
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: '',
      text,
      session_id: sessionId,
    });
  }

  /**
   * Confirm WIP task completion
   * Pattern: chatbot_{botCode}_taskId_{taskId}_wip_{repId}_{yes|no}
   */
  static async confirmWip(
    userId: string,
    orgId: string,
    confirm: 'yes' | 'no',
    taskContext: TaskContext,
    sessionId?: string
  ): Promise<ChannelResponse> {
    return this.processCommand({
      user_id: userId,
      org_id: orgId,
      command: ActionCommands.wipConfirm(taskContext, confirm),
      session_id: sessionId,
    });
  }
}
